import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateSelectMenuProps,
  harmonateGridActionIconSx,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';

/**
 * Spreadsheet template picker + "Template fields vs your file" match grid
 * (GL Sync upload pattern) for employee import dialogs.
 */
const EmployeeImportRefTemplateSection = ({
  templates = [],
  selectedTemplateId = '',
  onSelectTemplateId,
  activeTemplate = null,
  matchRows = [],
  sheetFields = [],
  onEditMapping,
  disabled = false,
  loading = false,
}) => {
  const hasDocTypeSetup = templates.length > 0 || loading;

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        bgcolor: ht.listBg,
        border: `1px solid ${ht.line}`,
        borderRadius: ht.fieldRadius,
        ...harmonateFormFieldsSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: ht.text }}>
        Spreadsheet template (optional)
      </Typography>

      {!hasDocTypeSetup && !loading ? (
        <Typography variant="body2" color="text.secondary">
          No Employee Import templates found. Create one under Reference Templates (document type
          and GL system are optional), then refresh this dialog.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <FormControl fullWidth size="small">
            <InputLabel id="employee-import-ref-template-label" shrink>
              Spreadsheet template
            </InputLabel>
            <Select
              labelId="employee-import-ref-template-label"
              label="Spreadsheet template"
              value={selectedTemplateId}
              onChange={(e) => onSelectTemplateId?.(e.target.value)}
              disabled={disabled || loading}
              displayEmpty
              notched
              MenuProps={harmonateSelectMenuProps}
            >
              <MenuItem value="">
                <em>None — match headers by name / alias</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={activeTemplate ? 'Edit column mapping' : 'Select a template first'}>
            <span>
              <IconButton
                onClick={onEditMapping}
                disabled={disabled || !activeTemplate}
                size="small"
                sx={{ mt: 0.5, ...harmonateGridActionIconSx }}
                aria-label="edit template mapping"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {activeTemplate && matchRows.length > 0 ? (
        <Box
          sx={{
            mt: 2,
            border: `1px solid ${ht.line}`,
            borderRadius: ht.fieldRadius,
            overflow: 'hidden',
            bgcolor: ht.white,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, px: 1.5, pt: 1.5, color: ht.text }}>
            Template fields vs your file
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', px: 1.5, pb: 1, color: ht.text }}>
            Left: template field and required Excel header. Right: matched column from the uploaded
            sheet.
          </Typography>
          {!sheetFields.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pb: 1.5 }}>
              Upload a file to compare headers.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0,
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  bgcolor: ht.listBg,
                  borderBottom: `1px solid ${ht.line}`,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: ht.text,
                }}
              >
                Template → required Excel header
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  bgcolor: ht.listBg,
                  borderBottom: `1px solid ${ht.line}`,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: ht.text,
                }}
              >
                Column in your file
              </Box>
              {matchRows.map((row, idx) => (
                <React.Fragment key={row.templateKey}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderBottom: idx < matchRows.length - 1 ? `1px solid ${ht.listBg}` : 'none',
                      borderRight: { xs: 'none', sm: `1px solid ${ht.listBg}` },
                      fontSize: '13px',
                      color: row.matched ? ht.green : ht.red,
                      bgcolor: row.matched ? 'rgba(2, 107, 29, 0.08)' : 'rgba(239, 63, 63, 0.06)',
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 600, color: 'inherit' }}>
                      {row.templateKey}
                    </Typography>
                    {' → '}
                    {row.expectedHeader || '—'}
                  </Box>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderBottom: idx < matchRows.length - 1 ? `1px solid ${ht.listBg}` : 'none',
                      fontSize: '13px',
                      color: row.matched ? ht.green : ht.red,
                      bgcolor: row.matched ? 'rgba(2, 107, 29, 0.08)' : 'rgba(239, 63, 63, 0.06)',
                    }}
                  >
                    {row.matchedHeader || '— missing —'}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default EmployeeImportRefTemplateSection;
